import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useCallback, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { ApplicationGet, ApplicationPut } from "../../../../models/Application"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../models/Authorization"
import { UserInfo } from "../../../../models/User"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  shortName: z.string().min(1, "Short is required"),
  description: z.string().min(1, "Description is required"),
})

export function ApplicationDetails(props: {
  readonly app: ApplicationGet
  readonly user: UserInfo
  readonly setApplication: (updatedApplication: ApplicationGet) => void
}) {
  const { app, user, setApplication } = props

  const formMethods = useForm<ApplicationPut>({
    resolver: zodResolver(validationSchema),
  })

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      name: app.name,
      shortName: app.shortName,
      description: app.description,
    })
  }, [app.description, app.name, app.shortName, reset])

  const onSubmit = useCallback(
    (values: ApplicationPut) =>
      digitalManagerApi
        .put<ApplicationGet>(`/applications/${app.id}`, {
          name: values.name,
          shortName: values.shortName,
          description: values.description,
        })
        .then((res) => {
          toast.success("Application updated")
          setApplication(res.data)
        })
        .catch(() => {
          toast.error("Cannot update application")
          throw new Error("Cannot update application")
        }),
    [app.id, setApplication]
  )

  const handleNameChange = useCallback(
    (field: ControllerRenderProps<ApplicationPut, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const handleShortNameChange = useCallback(
    (field: ControllerRenderProps<ApplicationPut, "shortName">) => (value: string) =>
      field.onChange(value),
    []
  )

  const handleDescriptionChange = useCallback(
    (field: ControllerRenderProps<ApplicationPut, "description">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderNameField = useCallback(
    ({ field }: { field: ControllerRenderProps<ApplicationPut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleNameChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleNameChange]
  )

  const renderShortNameField = useCallback(
    ({ field }: { field: ControllerRenderProps<ApplicationPut, "shortName"> }) => (
      <MUITextField
        id="shortName"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleShortNameChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.shortName?.message === "string"
            ? { shortName: { message: errors.shortName.message } }
            : undefined
        }
      />
    ),
    [errors.shortName, handleShortNameChange]
  )

  const renderDescriptionField = useCallback(
    ({ field }: { field: ControllerRenderProps<ApplicationPut, "description"> }) => (
      <MUITextField
        id="description"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleDescriptionChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.description?.message === "string"
            ? { description: { message: errors.description.message } }
            : undefined
        }
      />
    ),
    [errors.description, handleDescriptionChange]
  )

  const detailsContentProps: DiMaDetailsContentProps<ApplicationPut> = {
    formMethods,
    canWrite: hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      app.id,
      DimaFeatureShortNameEnum.AM,
      AccessLevel.Write
    ),
    inlineEdit: true,
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div className="title">{app.name}</div>,
        editItem: (
          <Controller
            name="name"
            control={formMethods.control}
            render={renderNameField}
          />
        ),
      },
      {
        id: 1,
        label: "Short",
        displayItem: <div className="title">{app.shortName}</div>,
        editItem: (
          <Controller
            name="shortName"
            control={formMethods.control}
            render={renderShortNameField}
          />
        ),
      },
      {
        id: 2,
        label: "Description",
        displayItem: <div className="title">{app.description}</div>,
        editItem: (
          <Controller
            name="description"
            control={formMethods.control}
            render={renderDescriptionField}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
